/*
 * ecom-sku-jump.js  (QW 2026-10-03)
 *
 * Exact part-number search without Algolia. When a search query looks like a
 * SKU / part number, ask Shopify's own predictive search (/search/suggest.json,
 * SKU field only, free) and confirm it against /products/<handle>.js. Only an
 * exact, single match redirects:
 *   - one variant whose SKU equals the query (case-insensitive) -> /products/<handle>?variant=<id>
 *   - or a base part number: suggest returns exactly one product and EVERY one of its
 *     variant SKUs is "<query>-<pack>" (e.g. 710004 -> 710004-25 / 710004-500) -> /products/<handle>
 * Anything else (no match, partial match, several matches, timeout, error) returns
 * null and the normal search (Algolia) runs as before.
 *
 * Used by: every form that submits to /search (capture-phase submit listener),
 * the header overlay (ecom-search-overlay-lazy.js), and the /search page
 * (sections/algolia-search.liquid) before it starts Algolia.
 */
(function () {
  'use strict';
  if (window.EcomSkuJump) return;

  var TIMEOUT_MS = 3000;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var memo = {};

  function norm(s) { return String(s == null ? '' : s).trim().toUpperCase(); }

  // One token, 4-40 chars, letters/digits with optional - or . separators, at least one digit.
  // Sizes such as 1/4-20, 3/8" or "M8 x 20" (slash, quote, space) never qualify.
  function looksLikeSku(q) {
    q = String(q == null ? '' : q).trim();
    if (q.length < 4 || q.length > 40) return false;
    if (!/^[A-Za-z0-9][A-Za-z0-9.\-]*[A-Za-z0-9]$/.test(q)) return false;
    return /\d/.test(q);
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  }

  function withTimeout(p, ms) {
    return new Promise(function (resolve) {
      var t = setTimeout(function () { resolve(null); }, ms);
      p.then(function (v) { clearTimeout(t); resolve(v); }, function () { clearTimeout(t); resolve(null); });
    });
  }

  function lookup(q) {
    var nq = norm(q);
    var url = root + 'search/suggest.json?q=' + encodeURIComponent(q.trim()) +
      '&resources[type]=product&resources[limit]=3' +
      '&resources[options][fields]=variants.sku&resources[options][unavailable_products]=last';
    return getJSON(url).then(function (d) {
      var products = (((d || {}).resources || {}).results || {}).products || [];
      if (!products.length) return null;
      return Promise.all(products.map(function (p) {
        return getJSON(root + 'products/' + encodeURIComponent(p.handle) + '.js').catch(function () { return null; });
      })).then(function (full) {
        full = full.filter(Boolean);
        var exact = [];
        full.forEach(function (p) {
          (p.variants || []).forEach(function (v) { if (v.sku && norm(v.sku) === nq) exact.push({ p: p, v: v }); });
        });
        if (exact.length === 1) {
          return root + 'products/' + exact[0].p.handle + '?variant=' + exact[0].v.id;
        }
        if (exact.length > 1) return null;
        // base part number: only when suggest found exactly one product
        if (products.length === 1 && full.length === 1) {
          var vs = full[0].variants || [];
          var all = vs.length > 0 && vs.every(function (v) {
            var s = norm(v.sku);
            return s.indexOf(nq + '-') === 0 && /^[A-Z0-9]+$/.test(s.slice(nq.length + 1));
          });
          if (all) return root + 'products/' + full[0].handle;
        }
        return null;
      });
    });
  }

  // Promise<string|null>: the product URL for an exact single match, else null. Never rejects.
  function resolve(q) {
    if (!looksLikeSku(q) || !window.fetch || !window.Promise) return Promise.resolve(null);
    var key = norm(q);
    if (Object.prototype.hasOwnProperty.call(memo, key)) return memo[key];
    try {
      var cached = sessionStorage.getItem('ecomSku:' + key);
      if (cached !== null) return (memo[key] = Promise.resolve(cached || null));
    } catch (e) {}
    memo[key] = withTimeout(lookup(q), TIMEOUT_MS).then(function (u) {
      try { sessionStorage.setItem('ecomSku:' + key, u || ''); } catch (e) {}
      if (u === null) delete memo[key]; // a timeout / miss may be retried later
      return u || null;
    });
    return memo[key];
  }

  window.EcomSkuJump = { looksLikeSku: looksLikeSku, resolve: resolve };

  // Any search form (header, hero, 404, empty search): jump on an exact part number.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM' || form.getAttribute('data-sku-jump') === 'off') return;
    var action = form.getAttribute('action') || '';
    if (!/\/search\/?(\?|$)/.test(action)) return;
    var input = form.querySelector('input[name="q"]');
    if (!input || !looksLikeSku(input.value)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var q = input.value;
    form.setAttribute('aria-busy', 'true');
    resolve(q).then(function (u) {
      form.removeAttribute('aria-busy');
      if (u) window.location.assign(u);
      else HTMLFormElement.prototype.submit.call(form); // native submit, no second submit event
    });
  }, true);
})();
